import { PropertyHintCoverageRow } from '~/services/ConnectionGoogleService';
import { Dayjs, toDayJs } from '~/services/TimeService';

// Fixed presentation bands, deliberately frontend-owned: changing them is a one-line
// edit with no API change, and age must be computed at render time — a backend-computed age
// would already be stale when the response lands.
export const AGE_BANDS = [
  { maxHours: 12, label: '<12h' },
  { maxHours: 24, label: '12–24h' },
  { maxHours: 48, label: '24–48h' },
  { maxHours: Infinity, label: '>48h' },
] as const;

export const CALENDAR_PAGE_DAYS = 28;

export function getAgeBandIndex(servedAt: string, now: Date): number {
  const ageHours = (now.getTime() - new Date(servedAt).getTime()) / (1000 * 60 * 60);
  return AGE_BANDS.findIndex((band) => ageHours < band.maxHours);
}

// Two units of precision (e.g. "16d 10h", "10h 5m"): enough to compare against the GHA price
// freshness column without implying a live-ticking clock — "now" is snapshotted on page load,
// so second-level precision would be stale the moment it renders.
export function formatAge(servedAt: string, now: Date): string {
  const ageMs = Math.max(0, now.getTime() - new Date(servedAt).getTime());
  const totalMinutes = Math.floor(ageMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export const coverageCellKey = (checkInDate: string, los: number): string => `${checkInDate}:${los}`;

/**
 * The day the hint was generated on, in the ACCOUNT's timezone — derived exactly as
 * checkInDate − abw, so no client-side timezone knowledge is needed. This can differ from the
 * served_at date displayed in local (AEST) time: a serve at 01/07 00:05 AEST is still 30/06 in
 * UTC, so its ABW 36 hint lands on the same check-in date as an ABW 35 hint served later that
 * same AEST day. Surfacing this day is what makes those "duplicate" rows self-explanatory.
 */
export function hintDay(row: PropertyHintCoverageRow): string {
  return toDayJs(row.checkInDate).subtract(row.abw, 'day').format('YYYY-MM-DD');
}

export interface CoverageCell {
  checkInDate: string;
  los: number;
  /** Most recent serve among the rows resolving to this (checkInDate, los). */
  lastServedAt: string;
  /** All contributing rows, so tooltips can show the ABW(s)/tier(s) behind the cell. */
  rows: Array<PropertyHintCoverageRow>;
}

export interface PropertyCoverageDerived {
  /** Distinct (checkInDate, los) pairs — NOT rows.length (see overlap note below). */
  itineraryCount: number;
  lastHintServedAt: string | null;
  oldestHintServedAt: string | null;
  losValues: Array<number>;
  cellMap: Map<string, CoverageCell>;
  /** Hinted share of the default calendar window: next 28 days × the LOS values present. */
  next28Days: { covered: number; total: number; pct: number };
  /** Itineraries (distinct cells) whose last hint is within 48h of now. */
  freshWithin48h: { count: number; pct: number };
}

/**
 * Derive everything the property view renders from the raw coverage rows, in one place so the
 * summary cards, calendar, and itinerary list can never disagree.
 *
 * Overlap note two rows can resolve to the same (checkInDate, los) via different ABWs —
 * e.g. served yesterday at ABW 2 vs served today at ABW 1 both point at tomorrow. The calendar
 * cell takes max(servedAt), and itineraryCount counts distinct (checkInDate, los) pairs rather
 * than rows.length. The itinerary list still shows every row.
 */
export function derivePropertyCoverage(rows: Array<PropertyHintCoverageRow>, now: Date): PropertyCoverageDerived {
  const cellMap = new Map<string, CoverageCell>();
  const losSet = new Set<number>();
  let lastHintServedAt: string | null = null;
  let oldestHintServedAt: string | null = null;

  for (const row of rows) {
    losSet.add(row.los);
    if (lastHintServedAt === null || row.servedAt > lastHintServedAt) {
      lastHintServedAt = row.servedAt;
    }
    if (oldestHintServedAt === null || row.servedAt < oldestHintServedAt) {
      oldestHintServedAt = row.servedAt;
    }

    const key = coverageCellKey(row.checkInDate, row.los);
    const cell = cellMap.get(key);
    if (!cell) {
      cellMap.set(key, { checkInDate: row.checkInDate, los: row.los, lastServedAt: row.servedAt, rows: [row] });
    } else {
      cell.rows.push(row);
      if (row.servedAt > cell.lastServedAt) {
        cell.lastServedAt = row.servedAt;
      }
    }
  }

  const losValues = Array.from(losSet).sort((a, b) => a - b);

  // Next-28-days coverage is scoped to the default calendar page (today..today+27 × the LOS
  // values present) so this number and the calendar the user first sees always agree.
  const todayStart = toDayJs(now).startOf('day');
  const windowStartStr = todayStart.format('YYYY-MM-DD');
  const windowEndStr = todayStart.add(CALENDAR_PAGE_DAYS, 'day').format('YYYY-MM-DD');
  let covered = 0;
  let freshCount = 0;
  for (const cell of cellMap.values()) {
    if (cell.checkInDate >= windowStartStr && cell.checkInDate < windowEndStr) {
      covered++;
    }
    if (now.getTime() - new Date(cell.lastServedAt).getTime() < 48 * 60 * 60 * 1000) {
      freshCount++;
    }
  }
  const next28Total = CALENDAR_PAGE_DAYS * losValues.length;

  return {
    itineraryCount: cellMap.size,
    lastHintServedAt,
    oldestHintServedAt,
    losValues,
    cellMap,
    next28Days: {
      covered,
      total: next28Total,
      pct: next28Total > 0 ? Math.round((covered / next28Total) * 100) : 0,
    },
    freshWithin48h: {
      count: freshCount,
      pct: cellMap.size > 0 ? Math.round((freshCount / cellMap.size) * 100) : 0,
    },
  };
}

/**
 * Calendar page (28 days per page) containing a check-in date, relative to page 0 starting today.
 * Negative pages are older dates.
 */
export function calendarPageForDate(checkInDate: string, today: Dayjs): number {
  const diffDays = toDayJs(checkInDate).diff(today.startOf('day'), 'day');
  return Math.floor(diffDays / CALENDAR_PAGE_DAYS);
}

export function calendarDatesForPage(page: number, today: Dayjs): Array<string> {
  const start = today.startOf('day').add(page * CALENDAR_PAGE_DAYS, 'day');
  return Array.from({ length: CALENDAR_PAGE_DAYS }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
}
